import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import type { ResumeSessionResponse } from "@/D2C/features/auth/utils/resumeJourney";
import { App_Url_New } from "@/shared/common/static";
import { axiosInstance } from "@/shared/lib/axiosInstance";
import { setSession } from "@/shared/redux/reducers/authSlice";

import ViewQuotePage from "../quotes/components/ViewQuotePage";

type LoadStatus = "idle" | "loading" | "success" | "error";

const isResumeSessionBody = (value: unknown): value is ResumeSessionResponse => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;

  const record = value as Record<string, unknown>;
  return Boolean(
    record.step_1_pre_request ||
    record.step_2_pre_request ||
    record.quote_request ||
    record.raw_request ||
    record.selected_quote ||
    record.lob_id
  );
};

const normalizeQuoteSessionPayload = (payload: any): ResumeSessionResponse => {
  const candidates = [
    payload,
    payload?.quote,
    payload?.session_data,
    payload?.session_quotes,
    payload?.data,
  ];

  const normalized = candidates.find(isResumeSessionBody);
  return (normalized || payload) as ResumeSessionResponse;
};

const ClientFetchQuote = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const mountedRef = useRef(true);

  const quoteId = params.get("quoteId");

  const [status, setStatus] = useState<LoadStatus>(() => (quoteId ? "loading" : "error"));
  const [sessionData, setSessionData] = useState<ResumeSessionResponse | null>(null);

  const fetchQuote = useCallback(async () => {
    if (!quoteId) {
      setStatus("error");
      return;
    }

    setStatus("loading");
    setSessionData(null);

    try {
      const res = await axiosInstance.get(
        `${App_Url_New.ApiEndPoint.getUserQuotes}/${encodeURIComponent(quoteId)}`
      );
      const data = normalizeQuoteSessionPayload(res.data?.quote ?? res.data);

      if (!mountedRef.current) return;

      setSessionData(data);
      dispatch(
        setSession({
          client: { id: String(data.client_id ?? data.client?.id ?? "") },
          session_data: { id: String(data.id ?? "") },
          session_quotes: data,
        })
      );
      setStatus("success");
    } catch (err) {
      if (!mountedRef.current) return;
      console.error("ClientFetchQuote:", err);
      setStatus("error");
    }
  }, [quoteId, dispatch]);

  useEffect(() => {
    mountedRef.current = true;

    void fetchQuote();

    return () => {
      mountedRef.current = false;
    };
  }, [fetchQuote]);

  if (status === "loading" || status === "idle") {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-theme-primary" />
        <p className="text-sm text-theme-text-soft">Loading quote details…</p>
      </div>
    );
  }

  if (status === "error" || !sessionData) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4">
        <p className="text-sm text-theme-text-soft">
          {quoteId ? "Unable to load the quote. Please try again." : "No quote ID provided."}
        </p>
        <div className="flex gap-3">
          {quoteId ? (
            <button
              onClick={() => {
                setStatus("loading");
                void fetchQuote();
              }}
              className="rounded-lg bg-theme-primary px-4 py-2 text-sm font-medium text-theme-text-inverse transition-colors hover:bg-theme-primary-hover"
            >
              Retry
            </button>
          ) : null}
          <button
            onClick={() => navigate(App_Url_New.B2CPageUrl.quotes)}
            className="rounded-lg border border-theme-border px-4 py-2 text-sm font-medium text-theme-text transition-colors hover:bg-theme-surface-muted"
          >
            Back to Quotes
          </button>
        </div>
      </div>
    );
  }

  return <ViewQuotePage sessionData={sessionData} />;
};

export default ClientFetchQuote;
